import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { BrandLogo } from '@/components/BrandLogo';
import { AnnouncementWizard } from '@/components/wizard/AnnouncementWizard';
import { OnboardingFlow, useOnboardingStatus } from '@/components/onboarding/OnboardingFlow';
import { PrivacyReassurance } from '@/components/home/PrivacyReassurance';
import { WhoIsThisForStep, readWizardPrefs, WIZARD_PREFS_KEY } from '@/components/wizard/WhoIsThisForStep';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: { backToDashboard: 'Back to Dashboard' },
  es: { backToDashboard: 'Volver al Panel' },
  fr: { backToDashboard: 'Retour au Tableau de Bord' },
  pt: { backToDashboard: 'Voltar ao Painel' },
  ar: { backToDashboard: 'العودة إلى لوحة التحكم' },
  ja: { backToDashboard: 'ダッシュボードに戻る' },
  ko: { backToDashboard: '대시보드로 돌아가기' },
  id: { backToDashboard: 'Kembali ke Dasbor' },
  hi: { backToDashboard: 'डैशबोर्ड पर वापस' },
};

export default function CreateAnnouncement() {
  const { needsOnboarding, isLoading, abTestVariant, completeOnboarding } = useOnboardingStatus();
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [isDemoInvite, setIsDemoInvite] = useState(false);
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const c = lc(copy, language);

  // "Who is this for" pre-flow: only show when no occasion/recipient query
  // params AND no prior selection in sessionStorage.
  const hasUrlPrefill =
    !!searchParams.get('occasion') ||
    !!searchParams.get('relationship') ||
    !!searchParams.get('template');
  const [preFlowDone, setPreFlowDone] = useState<boolean>(() => {
    if (hasUrlPrefill) return true;
    return !!readWizardPrefs();
  });

  useEffect(() => {
    if (sessionStorage.getItem('demo_invite') === 'true') {
      setIsDemoInvite(true);
    }
  }, []);

  // If we got URL params (e.g. from an occasion landing page), persist them
  // so the wizard prefill bridge picks them up uniformly.
  useEffect(() => {
    if (!hasUrlPrefill) return;
    const prefs = {
      relationship: searchParams.get('relationship') || undefined,
      template: searchParams.get('template') || undefined,
      animationTheme: searchParams.get('theme') || undefined,
      reason: searchParams.get('occasion') || undefined,
    };
    try { sessionStorage.setItem(WIZARD_PREFS_KEY, JSON.stringify(prefs)); } catch { /* noop */ }
  }, [hasUrlPrefill, searchParams]);

  const handleOnboardingComplete = (variant: 'full' | 'condensed') => {
    setOnboardingComplete(true);
    completeOnboarding();
  };

  // Show onboarding for first-time users
  if (!isLoading && needsOnboarding && !onboardingComplete) {
    return (
      <OnboardingFlow
        variant={abTestVariant}
        onComplete={handleOnboardingComplete}
      />
    );
  }

  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Simple header */}
      <header className="border-b border-border bg-background/95 backdrop-blur pt-[env(safe-area-inset-top)]">
        <div className="container flex h-16 items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm">{c.backToDashboard}</span>
          </Link>

          <Link to="/" className="hidden sm:block">
            <BrandLogo size="sm" />
          </Link>

          <div className="w-[120px]" />
        </div>
      </header>

      {/* Demo invite banner */}
      {isDemoInvite && (
        <div className="bg-primary/10 border-b border-primary/20">
          <div className="container flex items-center justify-center gap-2 py-3 px-4">
            <Sparkles className="h-4 w-4 text-primary shrink-0" />
            <p className="text-sm font-medium text-primary text-center">
              Start by creating your first message — it takes less than 60 seconds.
            </p>
          </div>
        </div>
      )}

      {/* Optional pre-flow */}
      {!preFlowDone ? (
        <WhoIsThisForStep
          onComplete={() => setPreFlowDone(true)}
          onSkip={() => setPreFlowDone(true)}
        />
      ) : (
        <>
          <div className="container max-w-xl py-3 text-center">
            <p className="text-xs text-muted-foreground italic">
              The strongest messages come from people who are strong within.
            </p>
          </div>

          <AnnouncementWizard />

          <div className="container max-w-xl pb-8">
            <PrivacyReassurance variant="compact" className="justify-center" />
          </div>
        </>
      )}
    </div>
  );
}
